import { useState } from "react";
import PatientProfilePopup from "./PatientProfile";
import AppointmentDetailModal from "../Appointments/AppointmentDetailModal";
import PaginationControls from "../CRMDashboard/PaginationControls";
import '../ScrollBar/ScrollBar.css'

interface AppointmentListProps {
  title: string; 
  description: string;
  appointments: any[];
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  itemsPerPage: number;
  onItemsPerPageChange: (items: number) => void;
}

export const AppointmentList: React.FC<AppointmentListProps> = ({
  title,
  description,
  appointments,
  currentPage,
  totalPages,
  onPageChange,
  itemsPerPage,
  onItemsPerPageChange,
}) => {
  const [patientId, setPatientId] = useState<string | null>(null);
  const [showProfilePopup, setShowProfilePopup] = useState(false);

  const handleProfileClick = (appointment: any) => {
    setShowProfilePopup(true);
    setPatientId(appointment.patientId);
  };

  // Enhanced Empty State
  const EmptyState = () => (
    <div className="flex flex-col items-center justify-center py-16 px-6 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl border-2 border-dashed border-gray-300">
      <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-lg mb-6">
        <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
        </svg>
      </div>
      <h3 className="text-xl font-bold text-gray-700 mb-3">
        No {title.toLowerCase()} found
      </h3>
      <p className="text-gray-500 text-center max-w-md leading-relaxed">
        When appointments are scheduled, they will appear here for easy management.
      </p>
    </div>
  );

  return (
    <div className="w-full max-w-7xl mt-16 mx-auto px-4 sm:px-6 lg:px-8">
      {showProfilePopup && (
        <PatientProfilePopup
          patientId={patientId !== null ? Number(patientId) : null}
          onClose={() => setShowProfilePopup(false)}
        />
      )}

      <div className="w-full space-y-6">
        {/* Enhanced Header Section */}
        <div className="relative overflow-hidden bg-gradient-to-r from-[#009A85]/10 via-[#009A85]/5 to-transparent rounded-2xl border border-[#009A85]/20 p-2 backdrop-blur-sm">
          <div className="relative z-10">
            <div className="flex items-center gap-4 ">
              <div className="w-1.5 h-8 bg-gradient-to-b from-[#009A85] to-[#007f6d] rounded-full"></div>
              <div className="flex-1">
                <h1 className="text-md sm:text-lg font-bold text-gray-900 mb-1">
                  {title}
                </h1>
                <div className="flex items-center gap-3">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-[#009A85]/10 text-[#009A85] border border-[#009A85]/20">
                    {appointments.length} {appointments.length === 1 ? 'appointment' : 'appointments'}
                  </span>
                </div>
              </div>
            </div>
            {/* <p className="text-gray-600 leading-relaxed max-w-3xl">
              {description}
            </p> */}
          </div>
          {/* Decorative Elements */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#009A85]/20 to-transparent rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-8 w-24 h-24 bg-gradient-to-tl from-[#007f6d]/20 to-transparent rounded-full blur-2xl"></div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
          {/* Desktop Table Header */}
          <div className="hidden sm:block bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
            <div className="grid grid-cols-4 lg:grid-cols-5 gap-4 px-6 py-4">
              <div className="col-span-2 lg:col-span-2">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span className="text-sm font-semibold text-gray-700">Patient Information</span>
                </div>
              </div>
              <div className="hidden lg:block">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
                  </svg>
                  <span className="text-sm font-semibold text-gray-700">Age</span>
                </div>
              </div>
              <div className="col-span-1">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-sm font-semibold text-gray-700">Date & Time</span>
                </div>
              </div>
              <div className="col-span-1 text-center">
                <span className="text-sm font-semibold text-gray-700">Actions</span>
              </div>
            </div>
          </div>

          {/* Appointments List */}
          <div className="max-h-[calc(100vh-325px)] overflow-y-auto custom-scrollbar">
            {appointments.length === 0 ? (
              <div className="p-8">
                <EmptyState />
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {appointments.map((appointment, index) => (
                  <div
                    key={`${appointment.appointmentId}-${index}`}
                    className="group hover:bg-gradient-to-r hover:from-[#009A85]/5 hover:to-transparent transition-all duration-300 ease-out"
                  >
                    {/* Mobile Layout */}
                    <div className="sm:hidden p-2 space-y-4">
                      <div className="flex items-start gap-4">
                       <div className="w-10 h-10 bg-gradient-to-br from-[#009A85] to-[#007f6d] rounded-full flex items-center justify-center text-white font-bold text-sm shadow-lg flex-shrink-0 group-hover:scale-105 transition-transform duration-200">
  {(() => {
    const [firstName, middleName] = appointment.patientName?.split(" ") || [];
    const firstInitial = firstName?.charAt(0)?.toUpperCase() || "";
    const middleInitial = middleName?.charAt(0)?.toUpperCase() || "";
    return firstInitial + middleInitial || "P";
  })()}
</div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-gray-900 text-lg leading-tight">
                            {appointment.patientName}
                          </h3>
                          <p className="text-sm text-gray-500 mt-1">
                            ID: {appointment.appointmentId} • {appointment.age} years
                          </p>
                          <div className="flex items-center gap-2 mt-2 text-sm text-gray-600">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="font-medium">
                              {appointment.formattedTime}, {appointment.formattedDate}
                            </span>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleProfileClick(appointment)}
                        className="w-full bg-gradient-to-r from-[#009A85] to-[#007f6d] hover:from-[#007f6d] hover:to-[#006652] text-white py-3 px-6 rounded-xl font-semibold text-sm transition-all duration-200 shadow-lg hover:shadow-xl hover:-translate-y-0.5 active:scale-95"
                      >
                        View Profile
                      </button>
                    </div>

                    {/* Desktop Layout */}
                    <div className="hidden sm:block">
                      <div className="grid grid-cols-4 lg:grid-cols-5 gap-4 px-6 py-2 items-center">
                        {/* Patient Info */}
                        <div className="col-span-2 lg:col-span-2">
                          <div className="flex items-center gap-4">
                           <div className="w-10 h-10 bg-gradient-to-br from-[#009A85] to-[#007f6d] rounded-full flex items-center justify-center text-white font-bold text-sm shadow-lg flex-shrink-0 group-hover:scale-105 transition-transform duration-200">
  {(() => {
    const [firstName, middleName] = appointment.patientName?.split(" ") || [];
    const firstInitial = firstName?.charAt(0)?.toUpperCase() || "";
    const middleInitial = middleName?.charAt(0)?.toUpperCase() || "";
    return firstInitial + middleInitial || "P";
  })()}
</div>
                            <div className="min-w-0 flex-1">
                              <h3 className="font-bold text-gray-900 text-base leading-tight truncate">
                                {appointment.patientName}
                              </h3>
                              <p className="text-sm text-gray-500 mt-1">
                                ID: {appointment.appointmentId}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Age */}
                        <div className="hidden lg:block">
                          <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-semibold bg-gray-100 text-gray-700 border border-gray-200 group-hover:bg-[#009A85]/10 group-hover:text-[#009A85] group-hover:border-[#009A85]/20 transition-all duration-200">
                            {appointment.age} yrs
                          </span>
                        </div>

                        {/* Date & Time */}
                        <div className="col-span-1">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
                              </svg>
                              <span className="text-sm font-semibold text-gray-900">
                                {appointment.formattedDate}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <span className="text-sm text-gray-600">
                                {appointment.formattedTime}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="col-span-1 flex justify-center">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleProfileClick(appointment)}
                              className="bg-gradient-to-r from-[#009A85] to-[#007f6d] hover:from-[#007f6d] hover:to-[#006652] text-white px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-200 shadow-md hover:shadow-lg hover:-translate-y-0.5 active:scale-95 group-hover:scale-105"
                            >
                              View Profile
                            </button>
                            {/* <button className="p-2 text-gray-400 hover:text-[#009A85] hover:bg-[#009A85]/10 rounded-lg transition-all duration-200">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                              </svg>
                            </button> */}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Enhanced Pagination */}
  
          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            itemsPerPage={itemsPerPage}
            onPageChange={onPageChange}
            onItemsPerPageChange={onItemsPerPageChange}
          />
       
      </div>
    </div>
  );
};

export default AppointmentList;